export enum PostLoginActionType {
  CHECK_EMAIL = 'CHECK_EMAIL',
  CHECK_BILLING_ADDRESS = 'CHECK_BILLING_ADDRESS',
  TAC_RECONFIRMATION = 'TAC_RECONFIRMATION',
}

export interface PostLoginAction {
  type: PostLoginActionType;
  timestamp: number;
  data?: {
    email?: string;
    billingAddress?: any;
  };
}

const STORAGE_KEY = 'postLoginActions';
const CHANGE_EVENT = 'postLoginActionsChanged';

/**
 * Utility for managing post-login actions in localStorage.
 * Actions are stored as a queue and processed sequentially.
 */
export const postLoginActionsStorage = {
  /**
   * The stored queue as it is serialized, a stable snapshot for `useSyncExternalStore`
   */
  getSerializedActions: (): string | null => {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  },

  parseActions: (serialized: string | null): PostLoginAction[] => {
    if (!serialized) return [];
    try {
      return JSON.parse(serialized);
    } catch {
      return [];
    }
  },

  /**
   * Get all pending actions from localStorage
   */
  getActions: (): PostLoginAction[] =>
    postLoginActionsStorage.parseActions(postLoginActionsStorage.getSerializedActions()),

  /**
   * Calls `onChange` when the queue changes, in this tab or another one
   */
  subscribe: (onChange: () => void) => {
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  },

  /**
   * Add action(s) to the queue
   */
  setActions: (actions: PostLoginAction[]): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
    // Dispatch custom event for reactive components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    }
  },

  /**
   * Remove first action from queue
   */
  removeFirstAction: (): void => {
    if (typeof localStorage === 'undefined') return;
    const actions = postLoginActionsStorage.getActions();
    const updated = actions.slice(1);
    if (updated.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    }
  },

  /**
   * Clear all actions from localStorage
   */
  clearActions: (): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    }
  },
};
